import { buildDailyMetricTrend } from "./buildDailyMetricTrend";
import { QUESTIONNAIRES } from "../constants";
import { formatDateParts } from "./recordingIdTime";
import {
  resolveCardiacQualityStatus,
  resolveRespiratoryQualityStatus,
} from "./resultQuality";
import { isPassingHrSignalQuality } from "./signalQuality";
import { getValidatedMeasurementValue } from "./validatedRanges";
import { RESULT_MODE_CONFIG } from "./resultModeConfig";
import {
  getQuestionnaireMaxScore,
  hasReportableScore,
  shouldShowQuestionnaireScoreToPatient,
} from "../questionnaireQuestions/questionnaireScores";

const CARDIAC_VARIANTS = new Set([
  RESULT_MODE_CONFIG.cardiopulmonary.variant,
  RESULT_MODE_CONFIG.pulse.variant,
  RESULT_MODE_CONFIG.sixMinuteWalk.variant,
]);

const RESPIRATORY_RATE_VARIANTS = new Set([
  RESULT_MODE_CONFIG.spirometry.variant,
  RESULT_MODE_CONFIG.cardiopulmonary.variant,
  RESULT_MODE_CONFIG.postExertion.variant,
  RESULT_MODE_CONFIG.sixMinuteWalk.variant,
]);

const SPIROMETRY_VARIANTS = new Set([
  RESULT_MODE_CONFIG.spirometry.variant,
]);

const FLOW_LOOP_VARIANTS = new Set([
  RESULT_MODE_CONFIG.spirometry.variant,
  RESULT_MODE_CONFIG.cardiopulmonary.variant,
]);
const QUESTIONNAIRE_META = Object.fromEntries(
  QUESTIONNAIRES.map(({ slug, labelKey }) => [slug, { labelKey }]),
);

function readFiniteNumber(value) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : null;
}

function resolveCombinedQualityState(...states) {
  const normalizedStates = states.filter(Boolean);

  if (normalizedStates.includes("fail")) {
    return "fail";
  }

  if (normalizedStates.includes("pass")) {
    return "pass";
  }

  return null;
}

function resolveCardiacMetricQualityState(row) {
  return resolveCardiacQualityStatus(
    row?.hrQuality,
    isPassingHrSignalQuality,
  );
}

function resolveRespiratoryMetricQualityState(row) {
  return resolveCombinedQualityState(
    resolveRespiratoryQualityStatus(row?.rrQuality),
    resolveRespiratoryQualityStatus(row?.signalQuality),
    resolveRespiratoryQualityStatus(row?.spirometryQuality),
  );
}

export function buildResultsTrendMetricDefinitions(t) {
  return [
    {
      key: "hr",
      featureKey: "hr",
      label: t("result.pulse.hrLabel", "Heart Rate"),
      unit: t("result.pulse.unit", "bpm"),
      icon: "ecg",
      decimals: 0,
      readValue: (row) => row?.hr,
      validateValue: (value) => getValidatedMeasurementValue("hr", value),
      getQualityState: resolveCardiacMetricQualityState,
      supportsObservation: (row) => CARDIAC_VARIANTS.has(row?.variant),
    },
    {
      key: "rr",
      featureKey: "rr",
      label: t("results.trends.metrics.rr", "Respiratory Rate"),
      unit: t("results.resp.rrUnit", "bpm"),
      icon: "air",
      decimals: 0,
      readValue: (row) => row?.rr,
      validateValue: (value) => getValidatedMeasurementValue("rr", value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => RESPIRATORY_RATE_VARIANTS.has(row?.variant),
    },
    {
      key: "hrv",
      featureKey: "hrv",
      label: t("result.pulse.hrvLabel", "HRV"),
      unit: t("result.pulse.hrvUnit", "ms"),
      icon: "monitor_heart",
      decimals: 0,
      readValue: (row) => row?.hrv,
      validateValue: (value) => getValidatedMeasurementValue("hrv", value),
      getQualityState: resolveCardiacMetricQualityState,
      supportsObservation: (row) => CARDIAC_VARIANTS.has(row?.variant),
    },
    {
      key: "fev1",
      featureKey: "fev1",
      label: t("results.spiro.fev1", "FEV1"),
      unit: t("results.spiro.FEV1Unit", "L"),
      icon: "pulmonology",
      decimals: 2,
      readValue: (row) => row?.fev1,
      validateValue: (value) => getValidatedMeasurementValue("fev1", value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => SPIROMETRY_VARIANTS.has(row?.variant),
    },
    {
      key: "fvc",
      featureKey: "fvc",
      label: t("results.spiro.fvc", "FVC"),
      unit: t("results.spiro.FVCUnit", "L"),
      icon: "airwave",
      decimals: 2,
      readValue: (row) => row?.fvc,
      validateValue: (value) => getValidatedMeasurementValue("fvc", value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => SPIROMETRY_VARIANTS.has(row?.variant),
    },
    {
      key: "pef",
      featureKey: "pef",
      label: t("results.spiro.pef", "PEF"),
      unit: t("results.spiro.PEFUnit", "L/m"),
      icon: "speed",
      decimals: 0,
      readValue: (row) => row?.pef,
      validateValue: (value) => getValidatedMeasurementValue("pef", value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => SPIROMETRY_VARIANTS.has(row?.variant),
    },
    {
      key: "fev1Fvc",
      featureKey: "fev1Fvc",
      label: t("results.spiro.fev1FvcRatio", "FEV1/FVC"),
      unit: null,
      icon: "sync_alt",
      decimals: 2,
      readValue: (row) => row?.fev1FvcRatio,
      validateValue: (value) =>
        getValidatedMeasurementValue("fev1FvcRatio", value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => SPIROMETRY_VARIANTS.has(row?.variant),
    },
    {
      key: "relativeChange",
      featureKey: "relativeChange",
      label: t("result.spiro.meanExhaleInhaleAreaRatio", "Inhale/Exhale Ratio"),
      unit: null,
      icon: "compare_arrows",
      decimals: 3,
      readValue: (row) => row?.meanExhaleInhaleAreaRatio,
      validateValue: (value) => readFiniteNumber(value),
      getQualityState: resolveRespiratoryMetricQualityState,
      supportsObservation: (row) => FLOW_LOOP_VARIANTS.has(row?.variant),
    },
  ];
}

function buildMetricObservation(definition, row) {
  const supportsObservation =
    typeof definition.supportsObservation === "function"
      ? definition.supportsObservation(row)
      : true;

  if (!supportsObservation) {
    return {
      row,
      rawValue: null,
      qualityState: null,
      validatedValue: null,
      approvedValue: null,
      hasObservation: false,
    };
  }

  const rawValue = readFiniteNumber(definition.readValue(row));
  const qualityState =
    typeof definition.getQualityState === "function"
      ? definition.getQualityState(row)
      : null;
  const validatedValue =
    typeof definition.validateValue === "function"
      ? definition.validateValue(rawValue, row)
      : rawValue;
  const approvedValue =
    validatedValue != null && qualityState !== "fail" ? validatedValue : null;

  return {
    row,
    rawValue,
    qualityState,
    validatedValue,
    approvedValue,
    hasObservation: rawValue != null || qualityState != null,
  };
}

function buildMetricIssue(observations) {
  const latestObservation = observations[0] || null;

  if (!latestObservation) {
    return null;
  }

  if (latestObservation.approvedValue != null) {
    return null;
  }

  if (latestObservation.qualityState === "fail") {
    return "quality";
  }

  return "unavailable";
}

function formatMetricValue(value, decimals) {
  if (value == null) {
    return null;
  }

  return decimals > 0 ? Number(value).toFixed(decimals) : String(Math.round(value));
}

function resolveDisplayValue(observation) {
  if (!observation) {
    return null;
  }

  if (observation.approvedValue != null) {
    return observation.approvedValue;
  }

  if (observation.validatedValue != null) {
    return observation.validatedValue;
  }

  return null;
}

function normalizeQuestionnaireId(value) {
  return typeof value === "string" ? value.trim().toLowerCase() : "";
}

function buildQuestionnaireTrendCardKey(questionnaireId) {
  const normalizedId = normalizeQuestionnaireId(questionnaireId) || "questionnaire";

  if (normalizedId === "cat") {
    return "catScore";
  }

  if (normalizedId === "sgrq") {
    return "sgrqScore";
  }

  return `${normalizedId}Score`;
}

function getQuestionnaireTrendLabel(questionnaireId, t) {
  const meta = QUESTIONNAIRE_META[normalizeQuestionnaireId(questionnaireId)];
  const fallbackLabel = questionnaireId || "Questionnaire";

  return meta?.labelKey ? t(meta.labelKey, fallbackLabel) : fallbackLabel;
}

function getQuestionnaireTimestamp(entry) {
  return (
    entry?.takenAt ||
    entry?.submittedAt ||
    entry?.clientSubmittedAt ||
    entry?.createdAt ||
    null
  );
}

function formatQuestionnaireScoreText(score, maxScore) {
  if (typeof score !== "number" || !Number.isFinite(score)) {
    return null;
  }

  return maxScore !== null ? `${score} / ${maxScore}` : String(score);
}

export function buildQuestionnaireTrendCardModels({
  entries,
  language,
  t,
  trendStartTimestamp = null,
  questionnaireAssignments = [],
} = {}) {
  const groupedEntries = new Map();

  (Array.isArray(entries) ? entries : []).forEach((entry) => {
    const questionnaireId = normalizeQuestionnaireId(entry?.questionnaireId);
    const score =
      typeof entry?.score === "number" && Number.isFinite(entry.score)
        ? entry.score
        : null;
    const maxScore =
      typeof entry?.maxScore === "number" && Number.isFinite(entry.maxScore)
        ? entry.maxScore
        : getQuestionnaireMaxScore(questionnaireId);
    const timestamp = getQuestionnaireTimestamp(entry);

    if (
      !questionnaireId ||
      !timestamp ||
      !hasReportableScore(
        questionnaireId,
        score,
        shouldShowQuestionnaireScoreToPatient(
          questionnaireId,
          questionnaireAssignments,
        ),
      )
    ) {
      return;
    }

    if (!groupedEntries.has(questionnaireId)) {
      groupedEntries.set(questionnaireId, []);
    }

    groupedEntries.get(questionnaireId).push({
      ...entry,
      questionnaireId,
      score,
      maxScore,
      timestamp,
    });
  });

  return Array.from(groupedEntries.entries()).map(([questionnaireId, group]) => {
    const sortedEntries = [...group].sort(
      (left, right) =>
        Date.parse(right.timestamp || "") - Date.parse(left.timestamp || ""),
    );
    const latestEntry = sortedEntries[0] || null;
    const trendEntries =
      trendStartTimestamp == null
        ? sortedEntries
        : sortedEntries.filter((entry) => {
            const timestampMs = Date.parse(entry.timestamp || "");
            return Number.isFinite(timestampMs) && timestampMs >= trendStartTimestamp;
          });
    const latestDate = latestEntry?.timestamp ? new Date(latestEntry.timestamp) : null;
    const latestParts = formatDateParts(latestDate, language);

    return {
      key: buildQuestionnaireTrendCardKey(questionnaireId),
      featureKey: `questionnaire:${questionnaireId}`,
      label: `${getQuestionnaireTrendLabel(questionnaireId, t)} score`,
      unit: null,
      icon: "assignment",
      decimals: 0,
      latestValue: latestEntry?.score ?? null,
      latestValueText: formatQuestionnaireScoreText(
        latestEntry?.score ?? null,
        latestEntry?.maxScore ?? null,
      ),
      latestDisplayValue: latestEntry?.score ?? null,
      latestDisplayValueText: formatQuestionnaireScoreText(
        latestEntry?.score ?? null,
        latestEntry?.maxScore ?? null,
      ),
      latestDisplayValueApproved: true,
      latestVariant: null,
      latestDate: latestParts.date || null,
      latestTime: latestParts.time || null,
      latestTimestamp: latestEntry?.timestamp || null,
      latestIssue: null,
      trend: buildDailyMetricTrend({
        rows: trendEntries,
        language,
        getValue: (entry) => entry.score,
        getTimestamp: (entry) => entry.timestamp,
      }),
      defaultSelected: false,
    };
  });
}

export function buildResultsTrendCardModels({
  rows,
  language,
  t,
  trendStartTimestamp = null,
} = {}) {
  const metricDefinitions = buildResultsTrendMetricDefinitions(t);
  const sourceRows = Array.isArray(rows) ? rows : [];

  return metricDefinitions
    .map((definition) => {
      const observations = sourceRows
        .map((row) => buildMetricObservation(definition, row))
        .filter((observation) => observation.hasObservation);

      if (!observations.length) {
        return null;
      }

      const latestApprovedObservation =
        observations.find((observation) => observation.approvedValue != null) || null;
      const latestDisplayObservation = observations[0] || null;
      const latestDisplayValue = resolveDisplayValue(latestDisplayObservation);
      const trendObservations =
        trendStartTimestamp == null
          ? observations
          : observations.filter((observation) => {
              const timestamp = Date.parse(observation.row?.timestamp || "");
              return (
                Number.isFinite(timestamp) && timestamp >= trendStartTimestamp
              );
            });
      const trend = buildDailyMetricTrend({
        rows: trendObservations,
        language,
        getValue: (observation) => observation.approvedValue,
        getTimestamp: (observation) => observation.row?.timestamp,
      });

      return {
        key: definition.key,
        featureKey: definition.featureKey,
        label: definition.label,
        unit: definition.unit,
        icon: definition.icon,
        decimals: definition.decimals,
        latestValue: latestApprovedObservation?.approvedValue ?? null,
        latestValueText: formatMetricValue(
          latestApprovedObservation?.approvedValue ?? null,
          definition.decimals,
        ),
        latestDisplayValue,
        latestDisplayValueText: formatMetricValue(
          latestDisplayValue,
          definition.decimals,
        ),
        latestDisplayValueApproved:
          latestDisplayObservation?.approvedValue != null,
        latestVariant: latestDisplayObservation?.row?.variant || null,
        latestDate: latestDisplayObservation?.row?.date || null,
        latestTime: latestDisplayObservation?.row?.time || null,
        latestTimestamp: latestDisplayObservation?.row?.timestamp || null,
        latestIssue: buildMetricIssue(observations),
        trend,
        defaultSelected: true,
      };
    })
    .filter(Boolean);
}
